import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  ACTIVITY_ENVIRONMENT_PATTERN,
  ACTIVITY_GUID_PATTERN,
  CopilotActivityError,
  listCopilotAgentActivityInputSchema,
  type CopilotActivityApi,
} from "../api/copilot-activity-api.js";

export const listCopilotAgentActivityTool: Tool = {
  name: "list_copilot_agent_activity",
  description:
    "EXPERIMENTAL: read one first page of Studio activity metadata for the exact agent and currently selected user only. Requires explicit environment matching the current Dataverse environment and an existing account object ID. Resolves the exact bot schema with a bounded silent fixed-select Dataverse read, then one silent Power Platform API metadata GET. Verifies every returned row's user and agent before projecting at most limit items. Returns only conversation GUIDs, validated timestamps, closed channel labels, bounded counts and unmapped numeric status codes; no summary, user ID, step details, transcript, URL or continuation token. Fixed wire pageSize=150 can exceed the local limit; never follows more pages and does not claim an exhaustive total or runtime/ticket success. No sign-in, claims handling, auth fallback, permission changes or agent execution. The observed Studio route and same-caller MCP authorization compatibility remain experimental.",
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      environment: {
        type: "string",
        pattern: ACTIVITY_ENVIRONMENT_PATTERN.source,
        description:
          "Exact current environment GUID or Default-GUID; no environment names, URLs or cross-environment fallback.",
      },
      agentId: { type: "string", pattern: ACTIVITY_GUID_PATTERN.source },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: 50,
        default: 20,
        description: "Local output limit; fixed first-page wire request remains pageSize=150.",
      },
    },
    required: ["environment", "agentId"],
  },
};

export async function handleListCopilotAgentActivity(
  context: { activityApi: CopilotActivityApi | null },
  input: unknown,
) {
  const parsed = listCopilotAgentActivityInputSchema.safeParse(input);
  if (!parsed.success) throw new CopilotActivityError("input");
  if (!context.activityApi) throw new CopilotActivityError("unavailable");
  const result = await context.activityApi.listActivity(parsed.data);
  return {
    text: `Read ${result.returnedCount} own-user activity metadata rows from one first page. Status codes are unmapped; no transcript, summary or execution-success verdict is returned. Additional pages are not followed. No tenant changes were made.`,
    structured: { copilotActivity: result },
  };
}
